import { ReactNode, useState } from 'react';
import {
  Collapse,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Radio,
  Typography,
} from '@material-ui/core';
import {
  DeleteOutline,
  Error as ErrorIcon,
  ExpandLess,
  ExpandMore,
  Visibility,
  VisibilityOff,
} from '@material-ui/icons';
import { FormikTextField, FormikRadioGroupField } from '@superdispatch/forms';
import {
  ColorDynamic,
  Column,
  Columns,
  Inline,
  Stack,
} from '@superdispatch/ui';
import { Box, Button, DescriptionItem, TextBox } from '@superdispatch/ui-lab';
import { useField } from 'formik';
import { SecuredIcon } from 'shared/icons/SecuredIcon';
import { ConfirmDialog } from 'shared/ui/ConfirmDialog';
import styled from 'styled-components';
import {
  CarrierEDIOptionsDTO,
  relationshipStatuses,
  VehiclesSetupPartnerDTO,
} from '../data/VehiclesDTO';

const RemovePartnerButton = styled(Button)`
  --button-text-color: ${ColorDynamic.Red500};
  --button-icon-color: ${ColorDynamic.Red500};

  &[aria-disabled='false'] {
    &:focus {
      --button-outline-color: ${ColorDynamic.Red30};
    }

    &:active {
      --button-background-color: ${ColorDynamic.Red50};
    }

    @media (hover: hover) and (pointer: fine) {
      &:hover {
        --button-text-color: ${ColorDynamic.Red500};
        --button-icon-color: ${ColorDynamic.Red500};
        --button-background-color: ${ColorDynamic.Red50};

        span svg {
          fill: ${ColorDynamic.Red500};
        }
      }
    }
  }
`;

const relationshipStatusLabels: Partial<Record<string, ReactNode>> = {
  moving_spot_loads: (
    <Inline space="xxsmall">
      <TextBox>Moving Spot Loads</TextBox>{' '}
      <TextBox color="secondary">(need integration)</TextBox>
    </Inline>
  ),
  under_contract: (
    <Inline space="xxsmall">
      <TextBox>Under Contract</TextBox>{' '}
      <TextBox color="secondary">(setup required)</TextBox>
    </Inline>
  ),
  in_discussions: (
    <Inline space="xxsmall">
      <TextBox>In Discussions</TextBox>{' '}
      <TextBox color="secondary">(future integration)</TextBox>
    </Inline>
  ),
};

const relationshipStatusSummaryLabels: Partial<Record<string, string>> = {
  moving_spot_loads: 'Moving Spot Loads',
  under_contract: 'Under Contract',
  in_discussions: 'In Discussions',
};

interface VehiclesSetupPartnerCardProps {
  name: string;
  /** Omitted for the last remaining partner, which cannot be removed. */
  onRemove?: () => void;
  usedOems: string[];
  options: undefined | CarrierEDIOptionsDTO;
}

export function VehiclesSetupPartnerCard({
  name,
  onRemove,
  usedOems,
  options,
}: VehiclesSetupPartnerCardProps) {
  const [{ value: partner }, meta] = useField<VehiclesSetupPartnerDTO>(name);
  const [expanded, setExpanded] = useState(true);
  const [isRemoveDialogOpen, setRemoveDialogOpen] = useState(false);
  const [isPasswordVisible, setPasswordVisible] = useState(false);
  // `meta.touched` on this nested object path only becomes truthy once a
  // field within this card has been interacted with (or the form was
  // submitted, which touches every field) — using it instead of always
  // showing `meta.error` avoids flagging a card the user hasn't reached yet.
  const hasErrors = !!meta.error && !!meta.touched;

  const oems = options?.oems || [];
  const oemLabel = oems.find((oem) => oem.value === partner.oem)?.label;
  const availableOems = oems.filter(
    (oem) => oem.value === partner.oem || !usedOems.includes(oem.value),
  );
  const availableRelationshipStatuses =
    options?.relationship_statuses || relationshipStatuses;

  return (
    <Box
      borderRadius="small"
      borderColor="Silver400"
      borderWidth="small"
      padding="medium"
    >
      <ConfirmDialog
        open={isRemoveDialogOpen}
        title="Remove Partner?"
        onClose={() => {
          setRemoveDialogOpen(false);
        }}
        cancelButtonProps={{ children: 'Cancel' }}
        confirmButtonProps={{
          children: 'Remove',
          variant: 'critical',
          onClick: () => {
            setRemoveDialogOpen(false);
            onRemove?.();
          },
        }}
      />

      <Stack space="small">
        <Columns align="center">
          <Column width="fluid">
            <Typography variant="subtitle2">
              {oemLabel || 'Partner Details'}
            </Typography>
          </Column>
          <Column width="content">
            <Inline space="small" verticalAlign="center">
              {expanded ? (
                onRemove && (
                  <RemovePartnerButton
                    variant="text"
                    size="small"
                    startIcon={<DeleteOutline fontSize="small" />}
                    onClick={() => {
                      setRemoveDialogOpen(true);
                    }}
                  >
                    Remove Partner
                  </RemovePartnerButton>
                )
              ) : hasErrors ? (
                <Inline space="xxsmall" verticalAlign="center">
                  <ErrorIcon color="error" fontSize="small" />
                  <Typography color="error">
                    Required fields left empty
                  </Typography>
                </Inline>
              ) : (
                <Typography color="textSecondary">
                  {[
                    (partner.relationship_status &&
                      relationshipStatusSummaryLabels[
                        partner.relationship_status
                      ]) ||
                      partner.relationship_status,
                    partner.expected_vins_per_month &&
                      `${partner.expected_vins_per_month} VINs/month`,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </Typography>
              )}
              <IconButton
                size="small"
                aria-label={expanded ? 'Collapse' : 'Expand'}
                onClick={() => {
                  setExpanded(!expanded);
                }}
              >
                {expanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Inline>
          </Column>
        </Columns>

        <Collapse in={expanded} unmountOnExit={true}>
          <Stack space="large">
            <Stack space="small">
              <FormikTextField
                name={`${name}.load_number_prefix`}
                label={
                  <Inline space="xxsmall">
                    <TextBox>Initial Load ID</TextBox>{' '}
                    <TextBox color="secondary">Optional</TextBox>
                  </Inline>
                }
                placeholder='e.g. "SD-" will appear before every EDI load ID'
                fullWidth={true}
              />

              <Columns space="small" collapseBelow="tablet">
                <Column width="1/2">
                  <FormikTextField
                    select={true}
                    name={`${name}.oem`}
                    label="OEM Partner Name"
                    fullWidth={true}
                  >
                    {availableOems.map((oem) => (
                      <MenuItem key={oem.value} value={oem.value}>
                        {oem.label}
                      </MenuItem>
                    ))}
                  </FormikTextField>
                </Column>
                <Column width="1/2">
                  <FormikTextField
                    name={`${name}.display_name`}
                    label={
                      <Inline space="xxsmall">
                        <TextBox>Partner Display Name</TextBox>{' '}
                        <TextBox color="secondary">Optional</TextBox>
                      </Inline>
                    }
                    helperText="The name shown on loads"
                    fullWidth={true}
                  />
                </Column>
              </Columns>

              <FormikTextField
                select={true}
                name={`${name}.expected_vins_per_month`}
                label={
                  <Inline space="xxsmall">
                    <TextBox>Expected VINs/Month</TextBox>{' '}
                    <TextBox color="secondary">Optional</TextBox>
                  </Inline>
                }
                fullWidth={true}
              >
                {(options?.expected_vins_per_month || []).map((value) => (
                  <MenuItem key={value} value={value}>
                    {value}
                  </MenuItem>
                ))}
              </FormikTextField>

              <Stack space="xxsmall">
                <Typography variant="h4">Status of the relationship</Typography>
                <FormikRadioGroupField name={`${name}.relationship_status`}>
                  <Stack space={['xsmall', 'none']}>
                    {availableRelationshipStatuses.map((status) => (
                      <FormControlLabel
                        key={status}
                        value={status}
                        control={<Radio />}
                        label={relationshipStatusLabels[status] || status}
                      />
                    ))}
                  </Stack>
                </FormikRadioGroupField>
              </Stack>
            </Stack>

            <Stack space="small">
              <Stack space="xxsmall">
                <Inline space="xxsmall">
                  <Typography variant="h4">OEM Contact</Typography>
                  <Typography variant="h4" color="textSecondary">
                    (Optional)
                  </Typography>
                </Inline>
                <Typography color="textSecondary">
                  Direct contact at the OEM provider with whom we can coordinate
                  to ensure smooth testing. Skip those fields if it&rsquo;s
                  unknown.
                </Typography>
              </Stack>
              <Columns space="small" collapseBelow="tablet">
                <Column width="1/2">
                  <FormikTextField
                    name={`${name}.oem_contact.name`}
                    label="Full Name"
                    fullWidth={true}
                  />
                </Column>
                <Column width="1/2">
                  <FormikTextField
                    name={`${name}.oem_contact.email`}
                    label="Email"
                    fullWidth={true}
                  />
                </Column>
              </Columns>
            </Stack>

            <Stack space="small">
              <Stack space="xxsmall">
                <Typography variant="h4">
                  Fenkell Vehi-Trac® Integration
                </Typography>
                <Typography color="textSecondary">
                  Certain OEMs require integration with Fenkell Vehi-Trac® for
                  claims or tracking purposes. If your OEM partner has specified
                  this, please provide your credentials so we can sync the data.
                </Typography>
                <Box paddingTop="small" paddingBottom="small">
                  <DescriptionItem icon={<SecuredIcon />}>
                    <TextBox color="secondary" variant="caption">
                      Your credentials are encrypted and stored securely.
                    </TextBox>
                  </DescriptionItem>
                </Box>
              </Stack>
              <Columns space="small" collapseBelow="tablet">
                <Column width="1/2">
                  <FormikTextField
                    name={`${name}.fenkell_username`}
                    label="Username"
                    fullWidth={true}
                  />
                </Column>
                <Column width="1/2">
                  <FormikTextField
                    name={`${name}.fenkell_password`}
                    label="Password"
                    fullWidth={true}
                    type={isPasswordVisible ? 'text' : 'password'}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label={
                              isPasswordVisible
                                ? 'Hide password'
                                : 'Show password'
                            }
                            onClick={() => {
                              setPasswordVisible(!isPasswordVisible);
                            }}
                          >
                            {isPasswordVisible ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Column>
              </Columns>
            </Stack>
          </Stack>
        </Collapse>
      </Stack>
    </Box>
  );
}
